/**
 * FoxchatBehaviorHost — tracks page behavior, evaluates foxchat_triggers, and
 * renders the resulting bubble/highlight/chat action.
 *
 * Mounts once inside AppLayout. Everything runs on the client; we never send
 * behavior metrics to the server, only evaluate them locally against the
 * rules pulled from Postgres.
 *
 * Respect order (fails shut):
 *   1. user_preferences.suggestions_enabled must be true
 *   2. user_preferences.quiet_mode must be false
 *   3. user_preferences.session_quiet_until must be past
 *   4. no other trigger has fired on this page+session yet
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { Sparkles, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { foxchat } from "@/lib/foxchat-dom";
import {
  loadActiveTriggers, matchesPattern, compileCondition, parseAction,
  hasFired, markFired, anyFiredOnPage,
  type TriggerRow, type TriggerAction,
} from "@/lib/foxchat-triggers";

const EVAL_INTERVAL_MS = 10_000;

interface Prefs {
  suggestions_enabled: boolean;
  quiet_mode: boolean;
  session_quiet_until: string | null;
}

export function FoxchatBehaviorHost() {
  const location = useLocation();
  const [triggers, setTriggers] = useState<TriggerRow[] | null>(null);
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [bubble, setBubble] = useState<{ message: string; highlight_id?: string | null; dismissible?: boolean; tab?: string } | null>(null);

  // Page-scoped counters — reset every pathname change.
  const mountedAt = useRef(Date.now());
  const clicks = useRef(0);
  const lastAction = useRef(Date.now());

  // Load rules + prefs once on mount.
  useEffect(() => {
    loadActiveTriggers().then(setTriggers);
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      const uid = u?.user?.id;
      if (!uid) { setPrefs({ suggestions_enabled: true, quiet_mode: false, session_quiet_until: null }); return; }
      const { data } = await supabase.from("user_preferences" as any)
        .select("suggestions_enabled, quiet_mode, session_quiet_until")
        .eq("user_id", uid).maybeSingle();
      setPrefs({
        suggestions_enabled: (data as any)?.suggestions_enabled ?? true,
        quiet_mode: (data as any)?.quiet_mode ?? false,
        session_quiet_until: (data as any)?.session_quiet_until ?? null,
      });
    })();
  }, []);

  // Reset counters whenever the user navigates. Dismiss any open bubble.
  useEffect(() => {
    mountedAt.current = Date.now();
    clicks.current = 0;
    lastAction.current = Date.now();
    setBubble(null);
  }, [location.pathname]);

  // Click + keypress counters, bounded to the window (not scoped to a specific element).
  useEffect(() => {
    const onClick = () => { clicks.current++; lastAction.current = Date.now(); };
    const onKey = () => { lastAction.current = Date.now(); };
    window.addEventListener("click", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const canFire = useMemo(() => {
    if (!prefs) return false;
    if (!prefs.suggestions_enabled) return false;
    if (prefs.quiet_mode) return false;
    if (prefs.session_quiet_until && new Date(prefs.session_quiet_until).getTime() > Date.now()) return false;
    return true;
  }, [prefs]);

  // Periodic evaluation loop.
  useEffect(() => {
    if (!triggers || !canFire) return;
    const pathname = location.pathname;
    const interval = setInterval(() => {
      if (anyFiredOnPage(pathname)) return; // one per page per session
      if (bubble) return; // already showing something
      const now = Date.now();
      const ctx = {
        time_on_page: Math.floor((now - mountedAt.current) / 1000),
        clicks_in_page: clicks.current,
        seconds_since_last_action: Math.floor((now - lastAction.current) / 1000),
        page_url: pathname,
        ...(window.__foxchatSignals || {}),
      };
      for (const t of triggers) {
        if (!matchesPattern(pathname, t.page_url_pattern)) continue;
        if (hasFired(t.id, pathname)) continue;
        const eval_ = compileCondition(t.condition);
        if (!eval_(ctx)) continue;
        const action = parseAction(t.action);
        if (!action) continue;
        runAction(action, t, pathname);
        break; // stop after the first match, respecting priority order
      }
    }, EVAL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [triggers, canFire, location.pathname, bubble]);

  const runAction = (action: TriggerAction, t: TriggerRow, pathname: string) => {
    markFired(t.id, pathname);
    // Best-effort analytics: bump fired_count + last_fired_at via RPC. Non-blocking.
    supabase.rpc("foxchat_trigger_fired" as any, { p_id: t.id }).then(() => {}, () => {});

    if (action.type === "open_chat") {
      window.dispatchEvent(new CustomEvent("foxchat:open"));
      return;
    }
    if (action.type === "highlight" && action.highlight_id) {
      foxchat.highlight(action.highlight_id).catch(() => {});
      return;
    }
    if (action.type === "open_resource_center") {
      window.dispatchEvent(new CustomEvent("foxchat:open_resource_center", { detail: { tab: action.open_resource_center_tab || null } }));
      return;
    }
    // Default: bubble. May also queue a highlight + resource-center tab.
    setBubble({
      message: action.message || "Need help with this page?",
      highlight_id: action.highlight_id || null,
      dismissible: action.dismissible !== false,
      tab: action.open_resource_center_tab,
    });
    if (action.highlight_id) foxchat.highlight(action.highlight_id).catch(() => {});
  };

  const openFromBubble = () => {
    if (bubble?.tab) {
      window.dispatchEvent(new CustomEvent("foxchat:open_resource_center", { detail: { tab: bubble.tab } }));
    } else {
      window.dispatchEvent(new CustomEvent("foxchat:open"));
    }
    setBubble(null);
  };

  if (!bubble) return null;

  return createPortal(
    <div
      role="dialog"
      aria-label="FoxChat suggestion"
      className="fixed z-[30] bottom-[72px] right-4 md:bottom-[92px] md:right-24 max-w-[320px] rounded-xl border border-amber-400/40 bg-card shadow-2xl p-3 flex items-start gap-2 animate-in slide-in-from-bottom-2"
    >
      <Sparkles className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-foreground leading-relaxed">{bubble.message}</p>
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={openFromBubble}
            className="text-[11px] font-semibold text-amber-400 hover:text-amber-300"
          >
            Show me →
          </button>
          {bubble.dismissible && (
            <button
              onClick={() => setBubble(null)}
              className="text-[10px] text-muted-foreground hover:text-foreground"
            >
              Not now
            </button>
          )}
        </div>
      </div>
      {bubble.dismissible && (
        <button
          onClick={() => setBubble(null)}
          aria-label="Dismiss"
          className="text-muted-foreground hover:text-foreground shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>,
    document.body,
  );
}
